/*
* Research components search for authenticated users
*/

import '@progress/kendo-ui';
import * as React from 'react';
import * as _ from 'lodash';
import moment from 'moment';
import Select from 'react-select';
import { Grid } from '@progress/kendo-grid-react-wrapper';
import { Row } from 'react-bootstrap';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { connectRequest } from 'redux-query';
import { withRouter } from 'react-router-dom';

import * as Actions from '../actions';
import * as AppComp from '../common/app-component-base';
import * as AppUtilities from '../common/apputilities';
import * as ReduxQueries from '../queries';
import { ApiQueryResponse } from '../queries/apiqueryresponse';
import ApiEndpoints from '../config/apiendpoints';
import AppConfiguration from '../config/configuration';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { DateRanges } from '../common/dateranges';
import { FormUtilities } from '../common/formutilities';
import { GridUtilities } from '../common/grid/gridutilities';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import SearchInput from '../common/searchInput';

class ResearchComponentsSearch extends AppComp.AppComponent {
    public MomentDateFormat: string = AppConfiguration.getMomentShortDateFormat();
    private gridDataSource: any = null;
    private gridWidget: any = null;
    private lastFiltersHash: string = '';

    public gridOptions = GridUtilities.kendoGridOptionsPaging(
        [
            GridUtilities.kendoGridColumnDefinition('categoryId', this.appTextGridColumnHeader('category-id'), 60, 0, undefined, null, undefined, 'truncate'),
            GridUtilities.kendoGridColumnDefinition('componentName', this.appTextGridColumnHeader('component-name'), 220, 1, undefined, null, undefined, 'truncate'),
            GridUtilities.kendoGridColumnDefinition('quantity', this.appTextGridColumnHeader('quantity'), 130, 2, undefined, null, undefined, 'truncate'),
            GridUtilities.kendoGridColumnDefinition('usefulLife', this.appTextGridColumnHeader('useful-life'), 60, 3, undefined, null, undefined, 'truncate'),
            GridUtilities.kendoGridColumnDefinition('avgCost', this.appTextGridColumnHeader('avg-cost'), 100, 4, undefined,
                                                    {
                                                        aggregates: ['average'],
                                                        footerTemplate: '#= typeof average !== "undefined" ? "Avg " + kendo.toString(average, "C") : "" #',
                                                        format: '{0:c}',
                                                        groupFooterTemplate: '#= typeof average !== "undefined" ? kendo.toString(average, "C") : "" #',
                                                    }),
            GridUtilities.kendoGridColumnDefinition('history', this.appTextGridColumnHeader('history'), 80, 5, undefined, null, undefined, 'truncate'),
            GridUtilities.kendoGridColumnDefinition('associationId', this.appTextGridColumnHeader('association-id'), 60, 6, undefined, null, undefined, 'truncate'),
            GridUtilities.kendoGridColumnDefinition('associationName', this.appTextGridColumnHeader('association-name'), 180, 7, undefined, null, undefined, 'truncate'),
            GridUtilities.kendoGridColumnDefinition('associationType', this.appTextGridColumnHeader('association-type'), 110, 8, undefined, null, undefined, 'truncate'),
            GridUtilities.kendoGridColumnDefinition('associationCity', this.appTextGridColumnHeader('association-city'), 110, 9, undefined, null, undefined, 'truncate'),
            GridUtilities.kendoGridColumnDefinition('associationState', this.appTextGridColumnHeader('association-state'), 70, 10, undefined, null, undefined, 'truncate'),
            GridUtilities.kendoGridColumnDefinition('viewOrderLink', this.appTextGridColumnHeader('view-order'), 80, 11,
                                                    GridUtilities.kendoGridLinkColumnTemplate('viewOrderLink', 'viewOrderLinkHref', this.appText('view-order-link-title')),
                                                    ),
            GridUtilities.kendoGridColumnDefinition('viewInventoryLink', this.appTextGridColumnHeader('view-inventory'), 80, 12,
                                                    GridUtilities.kendoGridLinkColumnTemplate('viewInventoryLink', 'viewInventoryLinkHref', this.appText('view-inventory-link-title')),
                                                    ),
        ],
        this.appText('no-results'),
        {
            groupable: true,
            filterable: true,
            scrollable: {
                virtual: false,
            },
            pageable: {
                refresh: true,
            },
        },
        this.appTextPageTitle()
    );

    public constructor(props: any) {
        super(props, 'app.research-components-search.');
    }

    public componentDidMount() {
        // Initialize filters hash - dataSource will be created in render()
        this.lastFiltersHash = this.getFiltersHash();
    }

    public componentWillUnmount() {
        this.props.dispatch(Actions.searchResearchComponentsClear());
        if (this.gridDataSource && typeof this.gridDataSource.destroy === 'function') {
            this.gridDataSource.destroy();
        }
        this.gridDataSource = null;
    }

    private getFiltersHash(): string {
        const filters = this.props.search.filters;
        return JSON.stringify({
            searchTerm: filters.searchTerm || '',
            componentId: filters.componentId || '',
            fromDate: filters.fromDate || '',
            toDate: filters.toDate || '',
        });
    }

    private createGridDataSource() {
        const apiUrl = getApiUrl();

        // Use arrow function to preserve 'this' context and always read latest props
        const readFunction = (options: any) => {
            // Always get latest filters and accessToken from props (not from closure)
            const accessToken = ReduxQueries.PropsAccessToken(this.props);
            const filters = this.props.search.filters;

            // Backend requires either searchTerm or componentId
            if (!filters.searchTerm && !filters.componentId) {
                options.success({
                    data: [],
                    total: 0,
                });
                return;
            }

            const { page, pageSize } = options.data;
            const pageNumber = page || 1;
            const limitNumber = pageSize || GridUtilities.KendoGridDefaultPageSize;

            // Build query parameters - match the format that works with backend
            const queryParams = new URLSearchParams();
            if (filters.searchTerm) {
                queryParams.append('searchTerm', filters.searchTerm);
            }
            // Always include componentId (even if empty) - backend expects it
            queryParams.append('componentId', filters.componentId || '');
            // Always include operator - backend expects it
            queryParams.append('operator', filters.operator || 'OR');
            if (filters.fromDate) {
                // Convert date to MM/DD/YYYY format (matching working API format)
                const fromDateValue = filters.fromDate;
                const fromDateObj = new Date(fromDateValue);
                if (!isNaN(fromDateObj.getTime())) {
                    // Format as MM/DD/YYYY
                    const month = ('0' + (fromDateObj.getMonth() + 1)).slice(-2);
                    const day = ('0' + fromDateObj.getDate()).slice(-2);
                    const year = fromDateObj.getFullYear();
                    queryParams.append('fromDate', `${month}/${day}/${year}`);
                } else {
                    queryParams.append('fromDate', fromDateValue);
                }
            }
            if (filters.toDate) {
                // Convert date to MM/DD/YYYY format (matching working API format)
                const toDateValue = filters.toDate;
                const toDateObj = new Date(toDateValue);
                if (!isNaN(toDateObj.getTime())) {
                    // Format as MM/DD/YYYY
                    const month = ('0' + (toDateObj.getMonth() + 1)).slice(-2);
                    const day = ('0' + toDateObj.getDate()).slice(-2);
                    const year = toDateObj.getFullYear();
                    queryParams.append('toDate', `${month}/${day}/${year}`);
                } else {
                    queryParams.append('toDate', toDateValue);
                }
            }
            queryParams.append('page', pageNumber.toString());
            queryParams.append('limit', limitNumber.toString());

            const url = `${apiUrl}?${queryParams.toString()}`;

            fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.json();
                })
                .then((data) => {
                    const apiResponse = new ApiQueryResponse(data);
                    const results = apiResponse.getResultsArray(true);
                    const totalCount = data.count || 0;

                    // Extend grid data
                    const extendedResults = this.extendGridData(results);

                    // Sort results (matching original logic)
                    const sortedResults = _.orderBy(extendedResults, ['history', 'research.date'], ['desc', 'desc']);

                    options.success({
                        data: sortedResults,
                        total: totalCount,
                    });
                })
                .catch((error) => {
                    console.error('Error fetching research components:', error);
                    options.error(error);
                });
        };

        // Destroy old DataSource if it exists
        if (this.gridDataSource && typeof this.gridDataSource.destroy === 'function') {
            this.gridDataSource.destroy();
        }

        this.gridDataSource = GridUtilities.kendoGridDataSourceServerPaged(
            readFunction,
            GridUtilities.KendoGridDefaultPageSize,
            {
                aggregate: [
                    { field: 'avgCost', aggregate: 'average' }
                ],
            }
        );
        // Don't call read() here - Grid will automatically call it when it mounts with this dataSource
    }

    private setGridRef = (grid: any) => {
        if (!grid) {
            this.gridWidget = null;
            return;
        }

        const widget = grid && grid.kendoWidget ? grid.kendoWidget() : null;
        this.gridWidget = widget;

        // Grid with server-side paging will automatically call read() when it mounts
        // No need to manually call read() here
        if (this.gridWidget && this.gridDataSource) {
            // Ensure dataSource is set (though it should already be set via props)
            this.gridWidget.setDataSource(this.gridDataSource);
        }
    };

    public extendGridData(records: Array<any>) {
        // Extend grid row data
        if (!records) { return []; }

        return records.map(r => {
            let record = _.cloneDeep(r);

            // Inventory & Research data
            const inventory = _.get(record, 'inventory', {});
            record.categoryId = _.get(inventory, 'component.componentCategoryId', 0);
            record.componentId = _.get(inventory, 'componentId', 0);
            record.componentName = _.get(inventory, 'componentName', '');

            record.quantity = _.get(inventory, 'quantity', '');
            record.usefulLife = _.get(inventory, 'usefulLife', ''); // changed from component.usefullife

            record.avgCost = _.get(record, 'invAvgCost', 0);
            record.history = (_.get(record, 'researchId', 0) > 0) ? this.appText('history-yes') : this.appText('history-no');

            // Association data
            const order = _.get(record, 'order', {});
            record.associationId = _.get(record, 'associationId', 0);
            record.associationName = _.get(order, 'association.name', '');
            record.associationType = _.get(order, 'association.associationType.name', '');
            record.associationCity = _.get(order, 'association.location.city', '');
            record.associationState = _.get(order, 'association.location.stateCode', '');

            // View order link
            record.orderId = _.get(record, 'orderId', 0);
            record.viewOrderLink = '';
            record.viewOrderLinkHref = '';
            if (record.orderId) {
                record.viewOrderLink = this.appText('view-order');
                record.viewOrderLinkHref = NavigationLinks.orderDetail(record.orderId);
            }

            // View inventory link
            record.inventoryId = _.get(record, 'inventoryId', 0);
            record.viewInventoryLink = '';
            record.viewInventoryLinkHref = '';
            if (record.associationId && record.inventoryId) {
                record.viewInventoryLink = this.appText('view-inventory');
                record.viewInventoryLinkHref = NavigationLinks.associationInventoryDetail(record.associationId, record.inventoryId);
            }

            return record;
        });
    }

    // HANDLERS
    public handleSelectBidSentRange(e: any) {
        const value = _.get(e, 'value', '');
        this.props.dispatch(Actions.searchResearchComponentsFilterDateRange(value));
    }

    public handleSelectFromDate(e: any) {
        const value = _.get(e, 'value', '');
        this.props.dispatch(Actions.searchResearchComponentsFilterFromDate(value));
    }

    public handleSelectToDate(e: any) {
        const value = _.get(e, 'value', '');
        this.props.dispatch(Actions.searchResearchComponentsFilterToDate(value));
    }

    public handleSearchTermEntry(e: any) {
        const value = _.get(e, 'target.value', '');
        this.props.dispatch(Actions.searchResearchComponentsFilterSearchTerm(value));
    }

    public handleComponentIdEntry(e: any) {
        const value = _.get(e, 'target.value', '');
        this.props.dispatch(Actions.searchResearchComponentsFilterComponentId(value));
    }

    // RENDERERS
    public render() {
        const currentFiltersHash = this.getFiltersHash();
        
        // Create or recreate dataSource if filters changed or dataSource doesn't exist
        if (!this.gridDataSource || this.lastFiltersHash !== currentFiltersHash) {
            this.lastFiltersHash = currentFiltersHash;
            // Destroy old dataSource if exists
            if (this.gridDataSource && typeof this.gridDataSource.destroy === 'function') {
                this.gridDataSource.destroy();
            }
            // Create new dataSource with current filters
            this.createGridDataSource();
        }

        const dataSource = this.gridDataSource;
        const gridKey = currentFiltersHash; // Use filters hash as key to force re-mount when filters change

        const bidRangeOptionsExtended = [
            { value: DateRanges.DateRangeOptionValues.ALL_DATES, label: this.appText('filter-date-range-options.all-dates') },
            { value: DateRanges.DateRangeOptionValues.LAST_THIRTY_DAYS, label: this.appText('filter-date-range-options.last-thirty-days') },
            { value: DateRanges.DateRangeOptionValues.MONTH_TO_DATE, label: this.appText('filter-date-range-options.month-to-date') },
            { value: DateRanges.DateRangeOptionValues.YEAR_TO_DATE, label: this.appText('filter-date-range-options.year-to-date') },
            { value: DateRanges.DateRangeOptionValues.PREVIOUS_MONTH, label: this.appText('filter-date-range-options.previous-month') },
            { value: DateRanges.DateRangeOptionValues.PREVIOUS_YEAR, label: this.appText('filter-date-range-options.previous-year') },
            { value: DateRanges.DateRangeOptionValues.LAST_THREE_MONTHS, label: this.appText('filter-date-range-options.last-three-months') },
            { value: DateRanges.DateRangeOptionValues.LAST_SIX_MONTHS, label: this.appText('filter-date-range-options.last-six-months') },
            { value: DateRanges.DateRangeOptionValues.LAST_TWELVE_MONTHS, label: this.appText('filter-date-range-options.last-twelve-months') },
            { value: DateRanges.DateRangeOptionValues.LAST_TWO_YEARS, label: this.appText('filter-date-range-options.last-two-years') },
            { value: DateRanges.DateRangeOptionValues.LAST_THREE_YEARS, label: this.appText('filter-date-range-options.last-three-years') },
            { value: DateRanges.DateRangeOptionValues.LAST_FOUR_YEARS, label: this.appText('filter-date-range-options.last-four-years') },
            { value: DateRanges.DateRangeOptionValues.LAST_FIVE_YEARS, label: this.appText('filter-date-range-options.last-five-years') },
            { value: DateRanges.DateRangeOptionValues.LAST_SIX_YEARS, label: this.appText('filter-date-range-options.last-six-years') },
        ];
        let bidRangeOptions = AppUtilities.isArrayWithLength(bidRangeOptionsExtended) ? FormUtilities.buildSelectOptions(bidRangeOptionsExtended, '', 'label', 'value') : [];

        const pageTitle = this.appTextPageTitle();
        const page = (
            <WhiteBoxPageSection title={pageTitle} >
                <Row>
                    <Col xs={12}>
                        <form onSubmit={e => { e.preventDefault(); }}>
                            <Row classname="form-group">
                                <Col xs={4} xl={3}>
                                    <div>
                                        <label className="control-label">{this.appText('filter-by-search-term')}</label>
                                        <SearchInput
                                            debounceTimeout={1000}
                                            onChange={(e) => { this.handleSearchTermEntry(e); }}
                                            placeholder={this.appText('enter-search-term')}
                                            value={this.props.search.filters.searchTerm}
                                        />
                                    </div>
                                </Col>
                                <Col xs={4} xl={3}>
                                    <div>
                                        <label className="control-label">{this.appText('filter-by-component-id')}</label>
                                        <SearchInput
                                            debounceTimeout={1000}
                                            onChange={(e) => { this.handleComponentIdEntry(e); }}
                                            placeholder={this.appText('enter-component-id')}
                                            value={this.props.search.filters.componentId}
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <Row className="form-group">
                                <Col xs={4} xl={3}>
                                    <div>
                                        <label className="control-label">{this.appText('filter-by-bid-sent-range')}</label>
                                        <Select
                                            clearable={false}
                                            name="bid-sent-range-filter"
                                            onChange={e => this.handleSelectBidSentRange(e)}
                                            options={bidRangeOptions}
                                            value={this.props.search.filters.dateRange}
                                        />
                                    </div>
                                </Col>
                                <Col xs={8}>
                                    {/*<Col xs={4} xl={3}>
                                        <div>
                                            <label className="control-label">{this.appText('filter-by-from-date')}</label>
                                            <DatePicker className="form-control" onChange={e => { this.handleSelectFromDate(e); }} selected={fromDateSelected} />
                                        </div>
                                    </Col>

                                    <Col xs={4} xl={3}>
                                        <div>
                                            <label className="control-label">{this.appText('filter-by-to-date')}</label>
                                            <DatePicker className="form-control" onChange={e => { this.handleSelectToDate(e); }} selected={toDateSelected} />
                                        </div>
                                    </Col>*/}
                                </Col>
                            </Row>
                        </form>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12}>
                        <Grid key={gridKey} ref={this.setGridRef} dataSource={dataSource} {...this.gridOptions} />
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );

        const { ...rest } = this.props;
        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = () => ApiEndpoints.getResearchComponentsSearchUrl();

const mapPropsToConfigs = (props) => {
    // Disable Redux Query - we're using server-side paging with Kendo Grid DataSource instead
    // Return null to prevent Redux Query from making API calls
    return null;
};

const mapStateToProps = (state, props) => {
    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(), state, props, {
        search: {
            results: state.entities.searchResearchComponentsResults,
            filters: {
                searchTerm: state.searchFiltersResearchComponents.searchTerm,
                componentId: state.searchFiltersResearchComponents.componentId,
                dateRange: state.searchFiltersResearchComponents.dateRange,
                fromDate: state.searchFiltersResearchComponents.fromDate,
                operator: 'OR',
                toDate: state.searchFiltersResearchComponents.toDate,
            }
        },
    });
};

const ResearchComponentsSearchContainer = compose<any>(
    connect(mapStateToProps),
    connectRequest(mapPropsToConfigs)
)(ResearchComponentsSearch);

export default withRouter(ResearchComponentsSearchContainer);



// WEBPACK FOOTER //
// ./src/research-components-search/index.tsx